import * as React from 'react'
import { createFormHook, useStore } from '@tanstack/react-form'
import type { FormState } from '@tanstack/form-core'
import type { Derived } from '@tanstack/store'
import type { ZodIssue, ZodSchema } from 'zod'

import { fieldContext, formContext, useFieldContext, useFormContext } from './form-context'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  CalendarIcon,
  CheckCircle,
  Clock,
  Loader2,
  XCircle,
  RefreshCw,
  Trash2,
  Upload,
  FileIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { UploaderConfig } from '@/lib/mediaUploadTypes'
import { useConfigParser, useUploaderState, useUploadQueue } from '@/hooks/useMediaUpload'
import { validateFiles, getErrorMessage, MAX_RETRIES } from '@/lib/mediaUploadUtils'
import { AppLogger } from '@/lib/logger'
import { useToast } from '@/contexts/ToastContext'

type Option = {
  value: string
  label: string
  description?: string
}

type BaseFieldProps = {
  label: string
  required?: boolean
  description?: string
  placeholder?: string
  disabled?: boolean
}

export type TextFieldProps = BaseFieldProps & {
  type?: React.ComponentProps<'input'>['type']
}

export type NumberFieldProps = BaseFieldProps & {
  min?: number
  max?: number
  step?: number
}

export type SelectFieldProps = BaseFieldProps & {
  options?: Option[]
  /**
   * Optional async loader for select options.
   * Useful for fetching options from APIs on demand.
   */
  loadOptions?: () => Promise<Option[]>
  disabled?: boolean
}

export type TextareaFieldProps = BaseFieldProps & {
  rows?: number
}

export type RadioGroupFieldProps = BaseFieldProps & {
  options: Option[]
  /**
   * Layout direction for radio items.
   */
  direction?: 'row' | 'column'
}

export type CheckboxFieldProps = BaseFieldProps & {
  /**
   * Optional label to render to the right of the checkbox when used as a single
   * boolean toggle field.
   */
  checkboxLabel?: string
}

export type DatePickerFieldProps = BaseFieldProps & {
  /**
   * Earliest year shown in the year dropdown.
   */
  fromYear?: number
  /**
   * Latest year shown in the year dropdown.
   */
  toYear?: number
}

export type MediaUploaderFieldProps = BaseFieldProps & {
  /**
   * Uploader configuration (UploaderConfig object or YAML string)
   */
  config: string | UploaderConfig
  /**
   * Entity ID for the upload (default: 'new')
   */
  entityId?: string
  /**
   * Enable mock mode for development/testing.
   * When true, simulates uploads without making actual API or storage calls.
   */
  mockMode?: boolean
}

export type SubmitButtonProps = {
  label: string
  variant?: 'default' | 'outline' | 'secondary' | 'destructive' | 'ghost'
}

/**
 * Generic text input field.
 * Uses shadcn `Input` and pulls value + meta from TanStack Form context.
 */
function TextField(props: TextFieldProps) {
  const { label, required, description, placeholder, type = 'text', disabled } = props
  const field = useFieldContext<any>()

  // Only show error if field has been touched
  const error = field.state.meta.isTouched ? field.state.meta.errors[0] : undefined

  return (
    <div className="space-y-1.5">
      <Label htmlFor={field.name}>
        {label}
        {required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
      </Label>
      <Input
        id={field.name}
        type={type}
        value={field.state.value ?? ''}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={Boolean(error) || undefined}
        className={cn(error && 'border-destructive')}
      />
      {description && !error && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/**
 * Radio group field built with shadcn `RadioGroup`.
 * Stores a single string value in the form state.
 */
function RadioGroupField(props: RadioGroupFieldProps) {
  const { label, required, description, options, direction = 'column', disabled } = props
  const field = useFieldContext<string>()

  const error = field.state.meta.errors[0]

  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
      </Label>
      <RadioGroup
        value={field.state.value ?? ''}
        onValueChange={(value) => field.handleChange(value)}
        disabled={disabled}
        className={cn(
          'gap-3',
          direction === 'row' ? 'flex flex-row flex-wrap items-center' : 'flex flex-col',
        )}
      >
        {options.map((opt) => (
          <div key={opt.value} className="space-y-1">
            <Label className="flex cursor-pointer items-center gap-2 text-sm">
              <RadioGroupItem value={opt.value} />
              <span>{opt.label}</span>
            </Label>
            {opt.description && (
              <p className="text-xs text-muted-foreground ml-6">{opt.description}</p>
            )}
          </div>
        ))}
      </RadioGroup>
      {description && !error && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/**
 * Checkbox field using shadcn `Checkbox`.
 * Stores a simple boolean value in the form state.
 */
function CheckboxField(props: CheckboxFieldProps) {
  const { label, required, description, checkboxLabel, disabled } = props
  const field = useFieldContext<boolean>()

  const error = field.state.meta.errors[0]
  const checked = Boolean(field.state.value)

  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
      </Label>
      <div className="flex items-center gap-2">
        <Checkbox
          checked={checked}
          onCheckedChange={(value) => field.handleChange(Boolean(value))}
          disabled={disabled}
          aria-invalid={Boolean(error) || undefined}
        />
        {checkboxLabel && <span className="text-sm">{checkboxLabel}</span>}
      </div>
      {description && !error && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

function formatDateDisplay(date: Date | undefined) {
  if (!date) return ''
  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

function parseDateFromValue(value: string | undefined): Date | undefined {
  if (!value) return undefined
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? undefined : d
}

/**
 * Date picker field built on top of the shadcn `Calendar` + `Popover` combo.
 * Stores the selected date as an ISO string (YYYY-MM-DD) in the form state.
 * The `fromYear` / `toYear` props control the year dropdown range.
 */
function DatePickerField(props: DatePickerFieldProps) {
  const { label, required, description, placeholder, fromYear, toYear, disabled } = props
  const field = useFieldContext<string | undefined>()

  const error = field.state.meta.isTouched ? field.state.meta.errors[0] : undefined
  const rawValue = field.state.value

  const initialDate = parseDateFromValue(rawValue)

  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(initialDate)
  const [month, setMonth] = React.useState<Date | undefined>(initialDate ?? new Date())
  const [inputValue, setInputValue] = React.useState(
    initialDate ? formatDateDisplay(initialDate) : '',
  )

  const handleSelect = (selected?: Date) => {
    setDate(selected)
    setMonth(selected ?? new Date())
    setInputValue(formatDateDisplay(selected))
    field.handleChange(selected ? selected.toISOString() : '')
    // Date selection happens in the popover, so blur may not fire on the input.
    // Mark as touched and run onBlur validators immediately.
    field.handleBlur()
    setOpen(false)
  }

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value
    setInputValue(value)

    const parsed = parseDateFromValue(value)
    if (parsed) {
      setDate(parsed)
      setMonth(parsed)
      field.handleChange(parsed.toISOString())
    } else {
      // Keep the raw string in the field, allowing Zod to validate/flag it
      field.handleChange(value as any)
    }
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={field.name}>
        {label}
        {required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
      </Label>
      <div className="relative flex gap-2">
        <Input
          id={field.name}
          value={inputValue}
          placeholder={placeholder ?? 'Select a date'}
          className={cn('bg-background pr-10', error && 'border-destructive')}
          onChange={handleInputChange}
          onBlur={field.handleBlur}
          disabled={disabled}
          aria-invalid={Boolean(error) || undefined}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setOpen(true)
            }
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              disabled={disabled}
              className="absolute right-2 top-1/2 size-8 -translate-y-1/2 p-0"
            >
              <CalendarIcon className="h-4 w-4" />
              <span className="sr-only">Select date</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto min-w-[400px] p-2"
            align="end"
            alignOffset={-8}
            sideOffset={10}
          >
            <Calendar
              className="p-3"
              mode="single"
              selected={date}
              month={month}
              onMonthChange={setMonth}
              onSelect={handleSelect}
              captionLayout="dropdown"
              fromYear={fromYear}
              toYear={toYear}
            />
          </PopoverContent>
        </Popover>
      </div>
      {description && !error && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/**
 * Numeric input field that stores its value as a string in the form state,
 * but is easy to coerce to a number on submit.
 */
function NumberField(props: NumberFieldProps) {
  const { label, required, description, placeholder, min, max, step, disabled } = props
  const field = useFieldContext<any>()

  // Only show error if field has been touched
  const error = field.state.meta.isTouched ? field.state.meta.errors[0] : undefined
  const value = field.state.value ?? ''

  return (
    <div className="space-y-1.5">
      <Label htmlFor={field.name}>
        {label}
        {required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
      </Label>
      <Input
        id={field.name}
        type="number"
        inputMode="numeric"
        value={value}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        aria-invalid={Boolean(error) || undefined}
        className={cn(error && 'border-destructive')}
      />
      {description && !error && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/**
 * Select field built on shadcn `Select`.
 */
function SelectField(props: SelectFieldProps) {
  const { label, required, description, placeholder, options, loadOptions, disabled } = props
  const field = useFieldContext<any>()

  // Only show error if field has been touched
  const error = field.state.meta.isTouched ? field.state.meta.errors[0] : undefined

  const [asyncOptions, setAsyncOptions] = React.useState<Option[] | null>(null)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!loadOptions) return

    let canceled = false
    setLoading(true)

    loadOptions()
      .then((result) => {
        if (!canceled) {
          setAsyncOptions(result)
        }
      })
      .catch(() => {
        if (!canceled) {
          // fall back to static options if loader fails
          setAsyncOptions(options ?? null)
        }
      })
      .finally(() => {
        if (!canceled) {
          setLoading(false)
        }
      })

    return () => {
      canceled = true
    }
  }, [loadOptions, options])

  const allOptions = asyncOptions ?? options ?? []

  // Convert field value to string for comparison with option values
  // Handle both numeric and string values from form state
  const fieldValue = field.state.value
  const normalizedValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '' 
    ? String(fieldValue) 
    : ''

  return (
    <div className="space-y-1.5">
      <Label htmlFor={field.name}>
        {label}
        {required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
      </Label>
      <Select
        value={normalizedValue}
        onValueChange={(value) => {
          field.handleChange(value)
          field.handleBlur() // Mark as touched to trigger validation
        }}
        disabled={disabled || loading}
      >
        <SelectTrigger
          id={field.name}
          aria-invalid={Boolean(error) || undefined}
          className={cn(error && 'border-destructive')}
        >
          <SelectValue placeholder={loading ? 'Loading...' : placeholder ?? 'Select...'} />
        </SelectTrigger>
        <SelectContent position="popper" sideOffset={4} style={{ zIndex: 9999 }}>
          {allOptions.length === 0 && !loading ? (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
              No options available
            </div>
          ) : (
            allOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {description && !error && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/**
 * Multiline text field using shadcn `Textarea`.
 */
function TextareaField(props: TextareaFieldProps) {
  const { label, required, description, placeholder, rows = 4, disabled } = props
  const field = useFieldContext<any>()

  const error = field.state.meta.errors[0]

  return (
    <div className="space-y-1.5">
      <Label htmlFor={field.name}>
        {label}
        {required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
      </Label>
      <Textarea
        id={field.name}
        rows={rows}
        value={field.state.value ?? ''}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={Boolean(error) || undefined}
        className={cn(error && 'border-destructive')}
      />
      {description && !error && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/**
 * Media uploader field that integrates with TanStack Form.
 * Fully self-contained with inlined UI components (DropZone, FileList, FileItem, ProgressBar, ImagePreview).
 * Manages file uploads internally and syncs successful uploads to form field value.
 * Supports both single-file (string) and multi-file (string[]) modes.
 */
function MediaUploaderField(props: MediaUploaderFieldProps) {
  const { label, required, description, config, entityId = 'new', disabled, mockMode } = props
  const field = useFieldContext<string | string[]>()
  const { toast } = useToast()
  const log = React.useMemo(() => AppLogger.getLogger('app-form/MediaUploaderField'), [])

  // Parse configuration
  const parsedConfig = useConfigParser(config)

  // State management
  const uploaderState = useUploaderState(parsedConfig.maxConcurrentUploads ?? 3)
  const {
    state,
    addFiles,
    removeFile,
    retryFile,
    hasUploadsInProgress,
    overallProgress,
  } = uploaderState

  // Upload queue
  const uploadQueue = useUploadQueue({
    config: parsedConfig,
    entityId,
    uploaderState,
    mockMode,
  })

  // ========================================================================
  // Image Preview State (moved from renderImagePreview)
  // ========================================================================
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null)
  const [hasPreviewError, setHasPreviewError] = React.useState(false)

  // ========================================================================
  // DropZone State (moved from renderDropZone)
  // ========================================================================
  const [isDragOver, setIsDragOver] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // ========================================================================
  // Image Preview Effect (moved from renderImagePreview)
  // ========================================================================
  // Note: This effect runs for all files, but we'll only use the first file's preview
  // in renderImagePreview based on the file passed in. The state is shared across all previews.
  const firstFile = state.files.length > 0 ? state.files[0].file : null
  React.useEffect(() => {
    if (!firstFile) {
      setPreviewUrl(null)
      return
    }

    if (!firstFile.type.startsWith('image/')) {
      setPreviewUrl(null)
      return
    }

    const url = URL.createObjectURL(firstFile)
    setPreviewUrl(url)
    setHasPreviewError(false)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [firstFile])

  // Sync successful uploads to field value
  React.useEffect(() => {
    const successfulFiles = state.files.filter((f) => f.status === 'success')
    if (successfulFiles.length > 0) {
      const isSingleFile = parsedConfig.maxFiles === 1
      const newValue = isSingleFile
        ? successfulFiles[0].result?.objectKey
        : successfulFiles.map((f) => f.result?.objectKey).filter((k): k is string => Boolean(k))
      if (newValue) {
        field.handleChange(newValue as any)
        log.debug('Upload results synced to field', {
          count: successfulFiles.length,
          isSingleFile,
        })
      }
    }
  }, [state.files, field, parsedConfig, log])

  // Handle files selected from DropZone
  const handleFilesSelected = React.useCallback(
    (files: File[]) => {
      if (disabled) return

      const validationResult = validateFiles(files, parsedConfig)

      validationResult.invalid.forEach((rejection) => {
        const message = rejection.message ?? getErrorMessage(rejection.errorCode ?? 'UNKNOWN')
        toast({
          title: 'File Rejected',
          description: `${rejection.file.name}: ${message}`,
          type: 'error',
        })
        log.warn('File rejected', {
          fileName: rejection.file.name,
          errorCode: rejection.errorCode,
        })
      })

      const currentFileCount = state.files.length
      const maxFiles = parsedConfig.maxFiles ?? Infinity
      const availableSlots = maxFiles - currentFileCount
      let filesToAdd = validationResult.valid

      if (filesToAdd.length > availableSlots) {
        toast({
          title: 'File Limit Exceeded',
          description: `Only ${availableSlots} more file(s) can be added. Maximum is ${maxFiles}.`,
          type: 'warning',
        })
        filesToAdd = filesToAdd.slice(0, availableSlots)
      }

      if (filesToAdd.length > 0) {
        addFiles(filesToAdd)
        log.info('Files added', { count: filesToAdd.length })

        if (parsedConfig.autoUpload) {
          setTimeout(() => {
            uploadQueue.processUploadQueue([...state.files])
          }, 0)
        }
      }
    },
    [disabled, parsedConfig, state.files, addFiles, toast, uploadQueue, log]
  )

  const handleRemove = React.useCallback(
    (id: string) => {
      uploadQueue.cancelUpload(id)
      uploadQueue.revokeObjectUrl(id)
      removeFile(id)
      log.debug('File removed', { id })
    },
    [removeFile, uploadQueue, log]
  )

  const handleRetry = React.useCallback(
    (id: string) => {
      retryFile(id)
      log.debug('File retry requested', { id })
      setTimeout(() => {
        uploadQueue.processUploadQueue(state.files)
      }, 0)
    },
    [retryFile, state.files, uploadQueue, log]
  )

  const handleUploadClick = React.useCallback(() => {
    if (disabled || hasUploadsInProgress) return
    const pendingFiles = state.files.filter((f) => f.status === 'pending')
    if (pendingFiles.length > 0) {
      log.info('Manual upload started')
      uploadQueue.processUploadQueue(state.files)
    }
  }, [disabled, hasUploadsInProgress, state.files, uploadQueue, log])

  // ========================================================================
  // DropZone Handlers (moved from renderDropZone)
  // ========================================================================
  const handleDragOver = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragOver(true)
    }
  }, [disabled])

  const handleDragLeave = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)
      if (!disabled) {
        const droppedFiles = Array.from(e.dataTransfer.files)
        if (droppedFiles.length > 0) {
          handleFilesSelected(droppedFiles)
        }
      }
    },
    [disabled, handleFilesSelected]
  )

  const handleFileInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files
      if (selectedFiles && selectedFiles.length > 0) {
        handleFilesSelected(Array.from(selectedFiles))
      }
      e.target.value = ''
    },
    [handleFilesSelected]
  )

  const handleButtonClick = React.useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [disabled])

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
        e.preventDefault()
        fileInputRef.current?.click()
      }
    },
    [disabled]
  )

  const hasPendingFiles = state.files.some((f) => f.status === 'pending')
  const error = field.state.meta.isTouched ? field.state.meta.errors[0] : undefined

  // ========================================================================
  // Render Helpers
  // ========================================================================

  /**
   * Render progress bar with variant based on state
   */
  const renderProgressBar = (progress: number, variant: 'default' | 'success' | 'error' = 'default') => {
    const clampedProgress = Math.min(100, Math.max(0, progress))
    return (
      <div
        role="progressbar"
        aria-valuenow={clampedProgress}
        aria-valuemin={0}
        aria-valuemax={100}
        className={cn('h-2 w-full overflow-hidden rounded-full bg-secondary')}
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            variant === 'default' && 'bg-primary',
            variant === 'success' && 'bg-green-500',
            variant === 'error' && 'bg-destructive'
          )}
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    )
  }

  /**
   * Render image preview thumbnail
   */
  const renderImagePreview = (file: File, size: number = 48) => {
    const isImage = file.type.startsWith('image/')

    if (!parsedConfig.showPreview) {
      return null
    }

    if (!isImage || hasPreviewError) {
      return (
        <div
          className="flex items-center justify-center rounded-md bg-muted"
          style={{ width: size, height: size }}
          aria-hidden="true"
        >
          <FileIcon className="size-6 text-muted-foreground" />
        </div>
      )
    }

    return (
      <div
        className="relative overflow-hidden rounded-md bg-muted"
        style={{ width: size, height: size }}
      >
        {previewUrl && (
          <img
            src={previewUrl}
            alt={`Preview of ${file.name}`}
            className="h-full w-full object-cover"
            onError={() => setHasPreviewError(true)}
          />
        )}
      </div>
    )
  }

  /**
   * Format file size to human-readable string
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  /**
   * Get status icon for file item
   */
  const getStatusIcon = (fileStatus: typeof state.files[0]['status']) => {
    switch (fileStatus) {
      case 'pending':
        return <Clock className="size-4 text-muted-foreground" aria-hidden="true" />
      case 'hashing':
        return <Loader2 className="size-4 animate-spin text-blue-500" aria-hidden="true" />
      case 'initiating':
      case 'uploading':
      case 'completing':
      case 'verifying':
        return <Loader2 className="size-4 animate-spin text-primary" aria-hidden="true" />
      case 'success':
        return <CheckCircle className="size-4 text-green-500" aria-hidden="true" />
      case 'error':
        return <XCircle className="size-4 text-destructive" aria-hidden="true" />
      default:
        return null
    }
  }

  /**
   * Get status text for file item
   */
  const getStatusText = (fileStatus: typeof state.files[0]['status'], errorMsg?: string): string => {
    switch (fileStatus) {
      case 'pending':
        return 'Pending'
      case 'hashing':
        return parsedConfig.labels?.calculating ?? 'Calculating checksum...'
      case 'initiating':
      case 'uploading':
      case 'completing':
      case 'verifying':
        return parsedConfig.labels?.uploading ?? 'Uploading...'
      case 'success':
        return parsedConfig.labels?.success ?? 'Upload complete'
      case 'error':
        return errorMsg ?? 'Upload failed'
      default:
        return ''
    }
  }

  /**
   * Render a single file item
   */
  const renderFileItem = (file: typeof state.files[0]) => {
    const canRemove = file.status === 'pending' || file.status === 'error'
    const canRetry = file.status === 'error' && file.retryCount < MAX_RETRIES

    return (
      <div
        key={file.id}
        className={cn(
          'flex items-center gap-4 rounded-lg border border-border bg-background p-3',
          file.status === 'error' && 'border-destructive/30 bg-destructive/5',
          file.status === 'success' && 'border-green-500/30 bg-green-500/5'
        )}
      >
        {renderImagePreview(file.file, 48)}

        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{file.file.name}</p>

          {parsedConfig.showFileSize && (
            <p className="text-xs text-muted-foreground">{formatFileSize(file.file.size)}</p>
          )}

          {file.status === 'uploading' && (
            <div className="mt-2">{renderProgressBar(file.progress, 'default')}</div>
          )}

          <div className="mt-1 flex items-center gap-2">
            {getStatusIcon(file.status)}
            <span
              className={cn(
                'text-xs',
                file.status === 'error' && 'text-destructive',
                file.status === 'success' && 'text-green-600',
                file.status !== 'error' && file.status !== 'success' && 'text-muted-foreground'
              )}
            >
              {getStatusText(file.status, file.error)}
            </span>

            {file.status === 'error' && file.retryCount > 0 && (
              <span className="text-xs text-muted-foreground">
                (Retry {file.retryCount}/{MAX_RETRIES})
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canRetry && (
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              onClick={() => handleRetry(file.id)}
              aria-label={`${parsedConfig.labels?.retry ?? 'Retry'} upload for ${file.file.name}`}
            >
              <RefreshCw className="size-4" />
            </Button>
          )}

          {canRemove && (
            <Button
              type="button"
              variant="destructive"
              size="icon-sm"
              onClick={() => handleRemove(file.id)}
              aria-label={`${parsedConfig.labels?.remove ?? 'Remove'} ${file.file.name}`}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </div>
      </div>
    )
  }

  /**
   * Render file list with progress summary
   */
  const renderFileList = () => {
    if (state.files.length === 0) {
      return null
    }

    const { totalFiles, completedFiles, failedFiles, activeUploads, overallPercent } = overallProgress

    const getProgressVariant = () => {
      if (failedFiles > 0 && activeUploads === 0 && completedFiles + failedFiles === totalFiles) {
        return 'error' as const
      }
      if (completedFiles === totalFiles && totalFiles > 0) {
        return 'success' as const
      }
      return 'default' as const
    }

    return (
      <div className="space-y-4">
        {(parsedConfig.showProgressBar !== false || parsedConfig.showSummaryText !== false) && (
          <div className="space-y-2">
            {parsedConfig.showSummaryText !== false && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {completedFiles} of {totalFiles} files uploaded
                  {activeUploads > 0 && ` (${activeUploads} in progress)`}
                </span>
                <span className="font-medium text-foreground">{overallPercent}%</span>
              </div>
            )}

            {parsedConfig.showProgressBar !== false && (
              renderProgressBar(overallPercent, getProgressVariant())
            )}
          </div>
        )}

        <div className="space-y-2">{state.files.map(renderFileItem)}</div>
      </div>
    )
  }

  /**
   * Render DropZone
   */
  const renderDropZone = () => {
    const acceptTypes = parsedConfig.allowedTypes?.join(',') ?? ''

    if (parsedConfig.dragAndDrop === false) {
      return null
    }

    return (
      <div
        role="region"
        aria-label={parsedConfig.labels?.dropzone ?? 'Drop files here or click to browse'}
        tabIndex={disabled ? -1 : 0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative flex flex-col items-center justify-center gap-4 p-8',
          'min-h-[200px] w-full rounded-lg border-2 border-dashed',
          'border-input bg-background/50',
          'transition-all duration-200 outline-none',
          !disabled && 'hover:border-primary/30 hover:bg-background/70',
          !disabled && 'focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/10',
          isDragOver && !disabled && 'border-primary bg-primary/5',
          disabled && 'pointer-events-none cursor-not-allowed opacity-50'
        )}
      >
        <p className={cn('text-center text-sm text-muted-foreground', isDragOver && 'text-primary')}>
          {parsedConfig.labels?.dropzone ?? 'Drop files here or click to browse'}
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptTypes}
          multiple={parsedConfig.multiFile ?? false}
          onChange={handleFileInputChange}
          disabled={disabled}
          className="sr-only"
          aria-hidden="true"
        />

        <Button
          type="button"
          variant="outline"
          onClick={handleButtonClick}
          disabled={disabled}
          aria-label={parsedConfig.labels?.button ?? 'Select Files'}
        >
          {parsedConfig.labels?.button ?? 'Select Files'}
        </Button>
      </div>
    )
  }

  // ========================================================================
  // Main Render
  // ========================================================================

  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
      </Label>

      <div role="region" aria-label="File uploader" className="space-y-4">
        {renderDropZone()}

        {parsedConfig.showFileList !== false && renderFileList()}

        {(() => {
          if (parsedConfig.showUploadButton === false) return null
          if (parsedConfig.showUploadButton === true && hasPendingFiles) {
            return (
              <Button
                type="button"
                variant="default"
                onClick={handleUploadClick}
                disabled={disabled || hasUploadsInProgress}
                className="w-full"
              >
                <Upload className="size-4 mr-2" />
                {hasUploadsInProgress ? 'Uploading...' : 'Upload Files'}
              </Button>
            )
          }

          if (parsedConfig.autoUpload === false && hasPendingFiles) {
            return (
              <Button
                type="button"
                variant="default"
                onClick={handleUploadClick}
                disabled={disabled || hasUploadsInProgress}
                className="w-full"
              >
                <Upload className="size-4 mr-2" />
                {hasUploadsInProgress ? 'Uploading...' : 'Upload Files'}
              </Button>
            )
          }

          return null
        })()}
      </div>

      {description && !error && <p className="text-xs text-muted-foreground">{description}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/**
 * Submit button that is automatically wired to the form's submit + validity status.
 * 
 * States:
 * - Disabled by default until form becomes valid
 * - Disabled while submitting
 * - Supports visual variants (default, outline, secondary, destructive)
 */
function SubmitButton(props: SubmitButtonProps) {
  const { label, variant = 'default' } = props
  const form = useFormContext()

  return (
    <form.Subscribe selector={(state) => [state.isSubmitting, state.isValidating, state.canSubmit]}>
      {([isSubmitting, isValidating, canSubmit]) => {
        // Use canSubmit provided by TanStack Form if available, otherwise fallback to simple validation check
        // Also perform a schema validation check if schema is available providing extra robustness
        const isDisabled = !canSubmit || Boolean(isSubmitting) || Boolean(isValidating)
        
        return (
          <Button
            // Use an explicit click handler to trigger TanStack Form submission.
            // This avoids relying on the native <form> submit event wiring.
            type="button"
            onClick={() => form.handleSubmit()}
            disabled={isDisabled}
            variant={variant}
            className="h-11 text-base font-semibold shadow-md hover:shadow-lg transition-all px-6"
            size="lg"
          >
            {isSubmitting ? (
              <>
                <svg className="mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              label
            )}
          </Button>
        )
      }}
    </form.Subscribe>
  )
}

/**
 * App-wide form hook + components, created via TanStack Form's
 * `createFormHook` composition API.
 *
 * This is the main entry-point for building forms in the app.
 */
const { useAppForm: useAppFormInternal, withForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    TextField,
    NumberField,
    SelectField,
    TextareaField,
    RadioGroupField,
    CheckboxField,
    DatePickerField,
    MediaUploaderField,
  },
  formComponents: {
    SubmitButton,
  },
})

type BaseFormInstance = ReturnType<typeof useAppFormInternal>

type AnyTanStackFormState<TValue> = FormState<TValue, any, any, any, any, any, any, any, any, any, any>

type SubmitFn<TValue> = (submission: { value: TValue } & Record<string, unknown>) => unknown | Promise<unknown>

type UseAppFormOptions<TValue> = Omit<Parameters<typeof useAppFormInternal>[0], 'defaultValues' | 'onSubmit'> & {
  defaultValues: TValue
  onSubmit?: SubmitFn<TValue>
  schema?: ZodSchema<TValue>
}

function applySchemaIssues(form: BaseFormInstance | null, issues: ZodIssue[]) {
  if (!form) return

  const fieldErrors = issues.reduce<Record<string, string[]>>((acc, issue) => {
    const field = issue.path?.[0]
    if (typeof field === 'string') {
      acc[field] = acc[field] ?? []
      acc[field].push(issue.message ?? 'Invalid value')
    }
    return acc
  }, {})

  Object.entries(fieldErrors).forEach(([field, errors]) => {
    form.setFieldMeta(field as any, (meta) => ({
      ...meta,
      errors,
    }))
  })
}

function clearSchemaErrors(form: BaseFormInstance) {
  const fieldMeta = form.state.fieldMeta
  Object.keys(fieldMeta).forEach((fieldName) => {
    const meta = fieldMeta[fieldName]
    if (meta?.errors?.length) {
      form.setFieldMeta(fieldName as any, (current) => ({
        ...current,
        errors: [],
      }))
    }
  })
}

function useAppFormEnhanced<TValue = any>(options: UseAppFormOptions<TValue>) {
  const { schema, onSubmit, ...rest } = options

  const schemaRef = React.useRef(schema)
  schemaRef.current = schema

  const formRef = React.useRef<BaseFormInstance | null>(null)

  const handleSubmit = React.useCallback(
    async (submission: any) => {
      const activeSchema = schemaRef.current
      if (activeSchema) {
        const parsed = activeSchema.safeParse(submission.value)
        if (!parsed.success) {
          applySchemaIssues(formRef.current, parsed.error.issues)
          return
        }
        return onSubmit?.({ ...submission, value: parsed.data })
      }

      return onSubmit?.(submission)
    },
    [onSubmit],
  )

  const schemaValidator = schema
    ? ({ value }: { value: TValue }) => {
        const result = schema.safeParse(value)
        return result.success ? undefined : 'Form has validation errors'
      }
    : undefined

  const form = useAppFormInternal({
    ...rest,
    validators: schemaValidator
      ? {
          onMount: schemaValidator,
          onChange: schemaValidator,
        }
      : rest.validators,
    onSubmit: handleSubmit,
  } as any)

  formRef.current = form
  const formValues = useStore(form.store as Derived<AnyTanStackFormState<TValue>>, (state) => state.values)

  React.useEffect(() => {
    const activeSchema = schemaRef.current
    const targetForm = formRef.current
    if (!activeSchema || !targetForm) return

    const result = activeSchema.safeParse(formValues)
    if (!result.success) {
      // Recompute full error map so stale field errors are removed.
      clearSchemaErrors(targetForm)
      applySchemaIssues(targetForm, result.error.issues)
    } else {
      clearSchemaErrors(targetForm)
    }
  }, [schema, formValues])

  // Compatibility helper: allow existing code to do `form.useStore(selector)`
  // (TanStack Form exposes `form.store`, but not all call sites use it directly.)
  const formWithStore = React.useMemo(() => {
    const useStoreCompat = <TSelected,>(selector: (state: AnyTanStackFormState<TValue>) => TSelected): TSelected =>
      useStore(form.store as any, selector as any) as TSelected

    return Object.assign(form, { useStore: useStoreCompat })
  }, [form])

  return formWithStore
}

export { useAppFormEnhanced as useAppForm, withForm, useFieldContext, useFormContext }


